import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Send, Loader2, Lock, MessageSquare, Clock, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navigation from "@/components/Navigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesPage = () => {
  const { user, loading: authLoading } = useAuth();
  const [messages, setMessages] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user && user.name) {
      axios.get(`${API}/messages/my`, { withCredentials: true })
        .then(r => setMessages(r.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/send`, { subject: subject.trim() || "Support Request", message: message.trim() }, { withCredentials: true });
      toast.success("Message sent to Brian Bass.");
      setSubject("");
      setMessage("");
      const res = await axios.get(`${API}/messages/my`, { withCredentials: true });
      setMessages(res.data);
    } catch {
      toast.error("Failed to send. Please try again.");
    } finally { setSending(false); }
  };

  if (authLoading) return <div className="min-h-screen bg-[#FDFBF7]"><Navigation /><div className="pt-28 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#4FB3D4]" /></div></div>;

  if (!user || !user.name) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Navigation />
        <div className="pt-28 pb-20 px-6 flex items-center justify-center">
          <Card className="w-full max-w-md rounded-2xl shadow-floating border-0 text-center">
            <CardContent className="p-10">
              <Lock className="h-10 w-10 text-[#A69BE0] mx-auto mb-4" />
              <h1 className="font-heading text-2xl font-bold text-[#1A2A3A] mb-3">Direct Message Support</h1>
              <p className="text-sm text-[#1A2A3A]/60 mb-6">Sign in to send a direct message to Brian Bass.</p>
              <Link to="/login"><Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-semibold">Sign In</Button></Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />
      <section className="pt-28 pb-20 px-6 lg:px-12" data-testid="messages-section">
        <div className="container mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center gap-2 text-[#4FB3D4] hover:text-[#4FB3D4]/80 mb-6 text-sm"><ArrowLeft className="h-4 w-4" /> Back</Link>

          <div className="text-center mb-8">
            <MessageSquare className="h-10 w-10 text-[#4FB3D4] mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold text-[#1A2A3A] mb-3" data-testid="messages-title">Direct Message Support</h1>
            <p className="text-sm text-[#1A2A3A]/60">Send a message directly to Brian Bass. Premium members get priority response.</p>
          </div>

          {/* Send Form */}
          <Card className="rounded-2xl shadow-card border border-[#E5E7EB]/50 mb-8" data-testid="message-form">
            <CardContent className="p-6">
              <form onSubmit={handleSend} className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Subject</label>
                  <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="What do you need help with?" className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm" data-testid="msg-subject" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#1A2A3A] mb-1">Your Message *</label>
                  <textarea value={message} onChange={e => setMessage(e.target.value)} required rows={5} placeholder="Write your message here..." className="w-full px-3 py-2.5 rounded-xl border border-[#E5E7EB] focus:border-[#4FB3D4] focus:outline-none text-sm resize-none" data-testid="msg-text" />
                </div>
                <Button type="submit" disabled={!message.trim() || sending} className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-semibold" data-testid="msg-send-btn">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {sending ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Message History */}
          <h2 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-4">Your Messages</h2>
          {loading ? <Loader2 className="h-6 w-6 animate-spin text-[#4FB3D4]" /> : messages.length === 0 ? (
            <p className="text-sm text-[#1A2A3A]/40 text-center py-6">No messages yet. Send your first one above.</p>
          ) : (
            <div className="space-y-3">
              {messages.map((m, i) => (
                <Card key={i} className="rounded-xl border border-[#E5E7EB]/50 bg-white" data-testid={`msg-${i}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <h3 className="font-semibold text-sm text-[#1A2A3A]">{m.subject}</h3>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${m.status === "replied" ? "bg-green-50 text-green-600" : "bg-amber-50 text-amber-600"}`}>
                        {m.status === "replied" ? <><CheckCircle className="h-3 w-3" /> Replied</> : <><Clock className="h-3 w-3" /> Pending</>}
                      </span>
                    </div>
                    <p className="text-sm text-[#1A2A3A]/60 mb-2">{m.message}</p>
                    {m.reply && (
                      <div className="bg-[#4FB3D4]/5 rounded-lg p-3 mt-2 border-l-2 border-[#4FB3D4]">
                        <p className="text-xs font-semibold text-[#4FB3D4] mb-1">Brian Bass replied:</p>
                        <p className="text-sm text-[#1A2A3A]/70">{m.reply}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-[#1A2A3A]/30 mt-2">{m.created_at}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      <footer className="py-8 px-6 bg-[#1A2A3A] text-white text-center">
        <p className="text-white/40 text-xs">&copy; 2025 ThryvLife by Brian Bass (BriVonB)</p>
      </footer>
    </div>
  );
};

export default MessagesPage;
